import { Component, computed, inject, Signal } from '@angular/core';
import { MenuController } from '@ionic/angular';
import { delay, filter, map, switchMap, take } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { ThemeService, UserService, BaseAppComponent, CustomLoader } from 'army-builder-shared';
import { register } from 'swiper/element/bundle';

import { environment } from '../environments/environment';
import pkg from '../../package.json';

import { RouterService } from './router.service';
import { FWWSettingsService } from './settings/settings.service';
import { GAME_IDS, GAME_ROLE_RESTRICTIONS } from './game-id.token';
import { Capacitor } from '@capacitor/core';
import { MenuItem } from 'army-builder-shared';
import { FalloutConfig } from './config';

register();

@Component({
    standalone: false,
    selector: 'app-root',
    template: `
        <!-- The visual theme class is mirrored onto ion-app (as well as the router
             outlet below) so overlays can see it: Ionic attaches modals, alerts,
             popovers and toasts to ion-app, outside the outlet, which otherwise
             leaves every FWW2E modal rendering in the default light palette on top
             of the themed screen. Only the *visual* theme goes here, never the game
             theme — no rule matches the visual theme on its own, so the shared app
             chrome above the outlet is unaffected. -->
        <ion-app [class]="[platformDescription, config.environment, (themeService.visualTheme$ | async) ?? ''].join(' ')">
            <div class="app-layout">
                <div class="warning-bars">
                    <div class="envWarning" *ngIf="config.environment === 'staging'">Staging environment</div>
                    <abs-impersonation-bar></abs-impersonation-bar>
                    <abs-billing-simulation-bar></abs-billing-simulation-bar>
                </div>
                <div class="content-area">
                    <ion-split-pane
                        contentId="mainRouterOutlet"
                        [when]="'lg'"
                        [disabled]="themeService.isDesktop()"
                        *ngIf="routeData$ | async as routeData"
                    >
                        <ion-menu
                            menuId="first"
                            type="overlay"
                            contentId="mainRouterOutlet"
                            *ngIf="appPages$ | async as appPages"
                            side="end"
                        >
                            <ion-header>
                                <ion-toolbar>
                                    <ion-title>{{ 'GLOBAL.MENU' | translate }}</ion-title>
                                </ion-toolbar>
                            </ion-header>
                            <ion-content>
                                <abs-menu
                                    style="list"
                                    [menuItems]="appPages"
                                    [mode]="'root'"
                                    [isLoggedIn]="(isLoggedIn$ | async) || false"
                                ></abs-menu>
                            </ion-content>
                            <ion-footer>
                                <a href="http://maloric.com" target="_blank" class="contact">{{
                                    'GLOBAL.MENU_ITEMS.MALORIC_DIGITAL' | translate
                                }}</a>
                                |
                                <a href="mailto:fallout@maloric.com" class="contact">{{ 'GLOBAL.MENU_ITEMS.CONTACT' | translate }}</a>
                                |
                                <span class="version">{{ version }}</span>
                            </ion-footer>
                        </ion-menu>
                        <div class="main">
                            <abs-sync-status-bar></abs-sync-status-bar>
                            <div class="topBar">
                                <a [routerLink]="'/'"><img src="/assets/images/logo.webp" alt="Fallout" class="falloutLogo" /></a>
                                <abs-menu
                                    [style]="'menu'"
                                    [menuItems]="(appPages$ | async) || []"
                                    [isLoggedIn]="(isLoggedIn$ | async) || false"
                                    [mode]="'root'"
                                    [layout]="'horizontal'"
                                ></abs-menu>
                            </div>

                            <div class="contentWrapper" [class.with-sidebar]="!routeData.hideSidebar">
                                <!-- <div class="sidebar" [class.hidden]="routeData.hideSidebar">
                            <ion-router-outlet
                                [class]="theme$ | async"
                                sidebar
                                id="sidebar"
                                name="sidebar"
                                contentId="mainRouterOutlet"
                            ></ion-router-outlet>
                        </div> -->
                                <ion-router-outlet [class]="theme$ | async" id="mainRouterOutlet" main></ion-router-outlet>
                            </div>

                            <div class="footer">
                                <a href="http://maloric.com" target="_blank" class="contact">{{
                                    'GLOBAL.MENU_ITEMS.MALORIC_DIGITAL' | translate
                                }}</a>
                                |
                                <a href="mailto:fallout@maloric.com" class="contact">{{ 'GLOBAL.MENU_ITEMS.CONTACT' | translate }}</a>
                                |
                                <a class="version" [routerLink]="'/pages/patch-notes'">{{ version }}</a>
                            </div>
                        </div>
                    </ion-split-pane>
                </div>
            </div>
            <div class="fullPageOverlay" *ngIf="supported === false">
                <ion-card>
                    <h1>{{ 'GLOBAL.UNSUPPORTED_CLIENT.TITLE' | translate }}</h1>
                    <p>
                        {{
                            'GLOBAL.UNSUPPORTED_CLIENT.MESSAGE'
                                | translate
                                    : {
                                          minClientVersion: this.minClientVersion,
                                          currentClientVersion: this.currentClientVersion
                                      }
                        }}
                    </p>
                </ion-card>
            </div>
            <abs-entity-preload-modal [gameIds]="gameIds()"></abs-entity-preload-modal>
            <abs-translation-preload-modal
                [gameIds]="gameIds()"
                [language]="language"
                [gameNames]="{ ff: 'Fallout: Factions', fww: 'Fallout: Wasteland Warfare', fww2e: 'Wasteland Warfare 2E' }"
            ></abs-translation-preload-modal>
        </ion-app>
    `,
    styles: [
        `
            .app-layout {
                display: flex;
                flex-direction: column;
                position: absolute;
                inset: 0;
            }

            .content-area {
                flex: 1;
                position: relative;
            }

            .envWarning {
                background: #ffcc00;
                color: black;
                font-weight: bold;
                width: 100%;
                height: 40px;
                line-height: 40px;
                text-align: center;
                font-family: 'Courier New', Courier, monospace;
            }

            ion-footer {
                padding: 5px;
                background: #5b5b5b;
                color: white;
            }

            ion-footer a {
                color: white;
            }

            ion-menu {
                background: none;
                --ion-color-primary: #444444;
            }

            .version,
            .contact {
                padding: 0 10px;
            }

            .unsupportedClient {
                position: fixed;
                display: flex;
                top: 0;
                bottom: 0;
                right: 0;
                left: 0;
                background: rgba(0, 0, 0, 1);
                align-items: center;
                justify-content: center;
            }

            .unsupportedClient h1 {
                font-family: Nidex;
                font-size: 1.4em;
                text-transform: uppercase;
            }

            .unsupportedClient ion-card {
                padding: 20px;
                text-align: center;
                max-width: 600px;
                font-family: Univers;
                font-size: 1.2em;
                line-height: 1.6em;
            }

            .main {
                display: flex;
                flex: 1 1 auto;
                --side-max-width: 100%;
                position: relative;
            }

            .mainRouterOutlet {
                flex-basis: 50%;
            }

            ion-router-outlet {
                position: relative;
                width: 100%;
            }

            @media screen and (min-width: 768px) {
                .mainRouterOutlet.halfWidth {
                    width: 50%;
                }
            }

            .contentWrapper {
                display: flex;
                flex: 1 1 auto;
                position: relative;
                overflow: hidden;
            }

            .contentWrapper > * {
                flex: 1 0 50%;
            }

            .sidebar {
                flex-basis: 50%;
                display: flex;
                width: 50%;
                transition: all 0.3s;
                transition-delay: 0.3s;
            }

            .sidebar.hidden {
                width: 0;
                --side-width: 0;
                flex-basis: 0;
                flex-grow: 0;
            }

            @media only screen and (max-width: 991px) {
                .topBar,
                .footer {
                    display: none;
                }

                .sidebar {
                    display: none;
                }

                .main {
                    flex-direction: column;
                }

                .contentWrapper {
                    flex-direction: column;
                }
            }

            @media screen and (min-width: 992px) {
                .content-area {
                    display: flex;
                    flex-direction: column;
                }

                ion-split-pane {
                    position: relative;
                    flex: 1;
                }

                :host {
                    --top-bar-height: 50px;
                }
                .topBar {
                    background: black;
                    display: flex !important;
                    width: 100%;
                    height: var(--top-bar-height);
                    flex: 0 0 var(--top-bar-height);
                    position: relative;
                    z-index: 2;
                }

                .topBar .falloutLogo {
                    height: calc(var(--top-bar-height) - 10px);
                    margin: 5px 20px 5px 9px;
                }

                .topBar abs-menu {
                    flex: 1 1 auto;
                }

                ion-menu {
                    display: none;
                }

                .footer {
                    background: #232323;
                    position: relative;
                    color: white;
                    text-align: center;
                    font-size: 12px;
                    z-index: 10;
                    height: 30px;
                    line-height: 30px;
                    flex: 0 0 30px;
                }

                .footer a {
                    color: #cfcfcf;
                    text-decoration: none;
                }

                .footer a:hover {
                    color: white;
                    transition:
                        all ease 0.3s,
                        text-decoration 0.1s ease;
                    text-decoration: underline;
                }

                .main {
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                    width: 100%;
                }

                .contentWrapper {
                    flex: 1 1 auto;
                    display: flex;
                    overflow: hidden;
                }

                ion-router-outlet {
                    flex: 1 1 auto;
                    position: relative;
                    z-index: 1;
                }

                abs-sync-status-bar {
                    position: absolute;
                    width: 100%;
                    top: var(--top-bar-height);
                }
            }
        `
    ]
})
export class AppComponent extends BaseAppComponent {
    version: string = environment.appVersion;
    supported = null;
    minClientVersion = null;
    currentClientVersion = null;
    routeData$ = this.routerService.routeData$;
    gameIds = computed(() => {
        const roles = this.userService.user()?.roles || [];
        return ['global', ...Object.values(GAME_IDS)].filter(
            (id) => !GAME_ROLE_RESTRICTIONS[id] || roles.includes(GAME_ROLE_RESTRICTIONS[id])
        );
    });
    appVersion: string = pkg.version;
    clearDataBeforeVersion: string = pkg.clearDataBeforeVersion;
    platformDescription = Capacitor.getPlatform();
    isLoggedIn$ = this.userService.loggedIn$;
    themeService = inject(ThemeService);
    config = inject(FalloutConfig);
    public appPages$: Observable<MenuItem[]> = combineLatest([
        this.userService.login$,
        this.featureFlagService.flags$,
        this.userService.isStaff$
    ]).pipe(
        map(([login, _flags, isAdmin]) => {
            const isLoggedIn = !!login.user;
            const showFww2e = this.featureFlagService.isFeatureVisible('fww2e')();
            const menuItems: MenuItem[] = [
                {
                    title: 'Fallout: Wasteland Warfare',
                    children: [
                        { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/fww/forces' },
                        { title: 'GLOBAL.MENU_ITEMS.BATTLE_TRACKER', icon: 'shield-outline', url: '/fww/battle' },
                        { title: 'GLOBAL.MENU_ITEMS.SETTLEMENTS', icon: 'home-outline', url: '/fww/settlements' },
                        { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'book-outline', url: '/fww/library' },
                        { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog-outline', url: '/fww/settings' }
                    ],
                    requiresLogin: false,
                    requiredRoles: []
                },
                {
                    title: 'Fallout: Factions',
                    children: [
                        { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/ff/forces' },
                        { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'book-outline', url: '/ff/library' },
                        { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog-outline', url: '/ff/settings' }
                    ]
                },
                ...(showFww2e
                    ? [
                          {
                              title: 'Wasteland Warfare 2E',
                              children: [
                                  { title: 'GLOBAL.MENU_ITEMS.CAMPAIGNS', icon: 'map-outline', url: '/fww2e/campaigns' },
                                  { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/fww2e/forces' },
                                  { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'book-outline', url: '/fww2e/library' },
                                  { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog-outline', url: '/fww2e/settings' }
                              ]
                          }
                      ]
                    : []),
                {
                    title: 'GLOBAL.MENU_ITEMS.EXTERNAL',
                    children: [
                        {
                            title: 'GLOBAL.MENU_ITEMS.FACEBOOK',
                            icon: 'logo-facebook',
                            url: 'https://www.facebook.com/groups/113493429210346/'
                        }
                    ]
                },
                {
                    title: 'GLOBAL.MENU_ITEMS.OTHER',
                    children: [
                        { title: 'Subscription', icon: 'cart-outline', url: '/subscription' },
                        {
                            title: 'GLOBAL.MENU_ITEMS.ABOUT',
                            icon: 'information-circle-outline',
                            url: '/pages/about'
                        },
                        {
                            title: 'GLOBAL.MENU_ITEMS.SETTINGS',
                            icon: 'cog',
                            url: '/settings'
                        },
                        {
                            title: 'GLOBAL.MENU_ITEMS.SUPPORT_TICKETS',
                            icon: 'flag-outline',
                            url: '/support',
                            requiresLogin: true
                        },
                        {
                            title: 'GLOBAL.MENU_ITEMS.PATCH_NOTES',
                            icon: 'list',
                            url: '/pages/patch-notes'
                        },
                        {
                            title: 'GLOBAL.MENU_ITEMS.KNOWN_ISSUES',
                            icon: 'bug-outline',
                            url: '/pages/known-issues'
                        },
                        {
                            title: 'GLOBAL.MENU_ITEMS.EULA',
                            icon: 'reader-outline',
                            url: '/eula'
                        }
                    ]
                }
            ];

            // Add placeholder for horizontal menu spacing
            menuItems.push({
                title: '',
                placeholder: true
            });

            if (isAdmin) {
                menuItems.push({
                    title: 'GLOBAL.MENU_ITEMS.ADMIN',
                    icon: 'create',
                    url: '/admin'
                });
            }

            if (isLoggedIn) {
                menuItems.push({
                    title: 'GLOBAL.LOGIN.BUTTONS.LOGOUT',
                    icon: 'log-out-outline',
                    onClick: () => this.userService.logout()
                });
            }

            return menuItems;
        })
    );
    public theme$ = combineLatest([this.themeService.theme$, this.themeService.visualTheme$]).pipe(
        map(([theme, visualTheme]) => [theme, visualTheme].filter(Boolean).join(' ')),
        delay(0)
    );
    constructor(
        // protected platform: Platform,
        protected userService: UserService,
        protected menu: MenuController,
        // protected http: HttpClientWithInFlightCache,
        // protected config: ArmyBuilderConfig,
        protected routerService: RouterService,
        protected settingsService: FWWSettingsService,
        // protected supportService: SupportService,
        // protected storage: NativeStorageService,
        // debugService: DebugService,
        // private store: Store,
        protected customLoader: CustomLoader
    ) {
        super();
        this.storage.init();
        this.userService.login$.subscribe((l) => {
            this.menu.enable(!!l.user);
        });
    }

    init() {}
}
